<template>
  <div class="is-align-items-center is-flex is-flex-direction-row">
    <div :class="{ 'cursor-pointer': canShowPreview }"
         @click="onOpenPreviewModal">
      <thumbnail class="info-thumbnail"
                 data-int="info-thumbnail"
                 :thumbnail-type="entityType"
                 :thumbnail-url="content.thumbnailUrlLarge" />
      <div v-if="canShowPreview"
           class="has-text-centered has-text-weight-bold">
        {{ $t('clickToEnlarge') }}
      </div>
    </div>
  </div>
  <two-d-modal v-show="is2dPreviewOpen"
               :filepath="filepathFor2d"
               :title="previewModalTitle"
               @close="onClosePreviewModal" />
</template>

<script setup lang="ts">
import TwoDModal from '@/components/library/content/TwoDModal.vue'
import { computed, onBeforeMount, Ref, ref } from 'vue'
import { Thumbnail } from '@/components/common'
import { useComposableForEsc } from '@/components/common/modal/useComposableForEsc'

const props = defineProps({
  content: {
    type: Object,
    required: true
  },
})

const entityType = props.content.contentType ||
  props.content.entityType ||
  props.content.mediaType ||
  props.content.type
const filepathFor2d: Ref<string> = ref('')
const is2dPreviewOpen = ref(false)
const previewModalTitle = ref('')

const canShowPreview = computed(() => {
  const supportedTypes = new Set(["part", "product-page"])
  if (!supportedTypes.has(entityType)) return false
  return !!filepathFor2d.value
})
const onClosePreviewModal = () => {
  is2dPreviewOpen.value = false
}
const onOpenPreviewModal = () => {
  if (!canShowPreview.value) return
  if (!filepathFor2d.value) return
  is2dPreviewOpen.value = true
}

onBeforeMount(async () => {
  if (!!props.content.thumbnailUrlLarge) {
    filepathFor2d.value = `${window.location.protocol}//${window.location.host}${props.content.thumbnailUrlLarge}`
  }

  if (!!props.content?.partNumber) {
    previewModalTitle.value = `${props.content.name} (${props.content.partNumber})`
  } else {
    previewModalTitle.value = `${props.content.name}`
  }
})

useComposableForEsc({
  dismissFunction: onClosePreviewModal,
  isActive: computed(() => is2dPreviewOpen.value)
})
</script>

<style scoped lang="scss">
.content-preview-wrapper {
  padding-top: 1em;
}
.info-thumbnail {
  height: 256px;
  margin: 1rem;
  object-fit: contain; // maintain original img ratios
  width: 256px;
}

.preview-buttons {
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1.75rem 0.75rem 1.75rem 1.75rem !important;

  .button {
    margin-right: unset !important;
  }
}

@media only screen and (max-width: 1300px) and (min-width: 769px) {
  .info-thumbnail {
    max-height: 16vh;
    margin: 0;
    max-width: 16vw;
  }
  .preview-buttons {
    margin: 1rem 0.75rem !important;
  }
}
@media only screen and (max-width: 768px) {
  .is-align-items-center {
    justify-content: center;
  }
}
img {
  -webkit-user-drag: none;
}
</style>
